import { format } from 'date-fns';
import { UseQueryResult } from 'react-query';
import Loading from '@/components/general/Loading';
import PTORequestsByTeamItem from '@/components/PTORequestsByTeamItem/PTORequestsByTeamItem.component';
import { usePTORequestsPageContext } from '@/pages/PTORequests/PTORequests.context';
import { EmptyState } from '@/pages/PTORequests/PTORequests.styles';
import { PTORequest, Team } from '@/types/api';
import { StaffMember } from '@/types/api/staffMember';
import { compareDateByDay, getFormatedDate, isTodayOrTomorrow } from '@/utils/dates';
import * as Styles from './PTORequestsScheduleList.styles';

type PTOScheduleOrganization = Record<
  string,
  Record<
    string,
    {
      team: Team;
      ptos: PTORequest[];
    }
  >
>;

const organizePtos = (
  ptos: PTORequest[],
  fetchStaffMembers: Record<string, UseQueryResult<StaffMember>>,
  currentOrganization?: PTOScheduleOrganization
): PTOScheduleOrganization => {
  const organization = currentOrganization || {};

  const sortedPtos = ptos.sort((a, b) => a.firstDay.getTime() - b.firstDay.getTime());

  if (sortedPtos.length === 0) return organization;

  const { firstDay } = sortedPtos[0];
  let lastDay = sortedPtos[0].entries[sortedPtos[0].entries.length - 1].date;

  for (let i = 1; i < sortedPtos.length; i += 1) {
    if (
      sortedPtos[i].entries[sortedPtos[i].entries.length - 1].date.getTime() > lastDay.getTime()
    ) {
      lastDay = sortedPtos[i].entries[sortedPtos[i].entries.length - 1].date;
    }
  }

  const cursor = new Date(firstDay);

  while (compareDateByDay(cursor, lastDay) <= 0) {
    const dateKey = getFormatedDate(cursor);

    if (!organization[dateKey]) organization[dateKey] = {};
    const organizationObject = organization[dateKey];

    for (let i = 0; i < sortedPtos.length; i += 1) {
      if (compareDateByDay(cursor, sortedPtos[i].firstDay) < 0) continue;
      if (
        compareDateByDay(cursor, sortedPtos[i].entries[sortedPtos[i].entries.length - 1].date) > 0
      )
        continue;

      const team = fetchStaffMembers[sortedPtos[i].staffMemberId].data?.team;

      if (team && organizationObject) {
        if (!organizationObject[team.id]) {
          organizationObject[team.id] = {
            team,
            ptos: [],
          };
        }

        organizationObject[team.id]?.ptos?.push(sortedPtos[i]);
      }
    }

    if (organizationObject && Object.keys(organizationObject).length === 0) {
      delete organization[dateKey];
    }

    cursor.setDate(cursor.getDate() + 1);
  }

  return organization;
};

type PTORequestsScheduleListProps = {
  ptos?: PTORequest[];
  isLoadingPTOs?: boolean;
  onUpdateRequest?: () => void;
};

const PTORequestsScheduleList = function ({
  ptos = [],
  isLoadingPTOs = false,
  onUpdateRequest,
}: PTORequestsScheduleListProps): JSX.Element {
  const { fetchStaffMembers } = usePTORequestsPageContext();
  const isLoading =
    isLoadingPTOs ||
    Object.keys(fetchStaffMembers).some(
      (staffMemberId) => fetchStaffMembers[staffMemberId].isLoading
    );
  const organizedPtos = organizePtos(isLoading ? [] : ptos, fetchStaffMembers);

  if (isLoading) {
    return <Loading />;
  }

  const dateKeys = Object.keys(organizedPtos);

  return (
    <>
      {dateKeys.length === 0 ? (
        <EmptyState
          title="There’s no scheduled PTO to show"
          description="Staff members’ upcoming PTO will be displayed here once it’s approved by their managers."
        />
      ) : (
        dateKeys.map((dateKey) => {
          const date = new Date(dateKey);
          const customDateLabel = isTodayOrTomorrow(date);

          return (
            <Styles.PTOScheduleGroup key={dateKey}>
              <Styles.PTOScheduleGroupTitle>
                <strong className="capitalize">{customDateLabel || format(date, 'EEEE')}</strong>
                {!customDateLabel && format(date, 'MMMM d')}
              </Styles.PTOScheduleGroupTitle>
              <Styles.PTOScheduleList allowMultiple>
                {Object.keys(organizedPtos[dateKey] || {}).map((teamId) => (
                  <PTORequestsByTeamItem
                    key={teamId}
                    team={organizedPtos[dateKey][teamId].team}
                    ptos={organizedPtos[dateKey][teamId].ptos}
                    fetchStaffMembers={fetchStaffMembers}
                    date={new Date(dateKey)}
                    onUpdateRequest={onUpdateRequest}
                  />
                ))}
              </Styles.PTOScheduleList>
            </Styles.PTOScheduleGroup>
          );
        })
      )}
    </>
  );
};

export default PTORequestsScheduleList;
